/* eslint-disable max-depth */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/*
AdobePatentID="P8158-US"
*/

// @ts-nocheck
import https from 'https';
import getConfig from '../config';
import SearchAnalytics from '../analytics/SearchAnalytics';
import SearchFactory from './search-factory/SearchFactory';
import { SearchError, SearchManager, SearchResults } from './search-manager';
import GlobalsUtils from '../utils/GlobalsUtils';
import PerformanceUtils from '../utils/PerformanceUtils';
import FileTypeService from './FileTypeService';
import GeoService from './GeoService';
import SearchUtils from '../utils/SearchUtils';
import Request from '../utils/Request';
import { Utils } from '../utils/Globals';
import WebStorage from '../storage/WebStorage';
const uuid = require('uuid/v1');

import { getLogger } from '../utils/SearchLogger';
import AnalyticsUtil from '../analytics/AnalyticsUtil';

import * as Autocomplete from './autocomplete';
import * as UserPreferences from './user-preferences';
import * as SearchHistory from './search-history';
import * as SearchApps from './search-apps';
import UserPreferencesModel from '../personalization/UserPreferencesModel';
class SearchService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Returns a SearchManager SearchResults promise that can be used to retrieve search results (if the IMS data is invalid, it will be a SearchResults with no items).
	 * @param {Object} searchParams - search paramaters that each searchable will modify (in a copy of the object) to suit its needs
	 * @param {string} query - the search term query
	 * @param {FilterItems} filter - a SearchManager filter
	 * @param {Sort} sort - a SearchManager Sort
	 * @param {number} startIndex - the search start index
	 * @param {number} limit - the search result limit
	 * @returns {Promise<SearchResults>} - a SearchManager SearchResults promise that can be used to retrieve search results (if the IMS data is invalid, it will be a SearchResults with no items)
	 */
	fetchSearchAssetsResults({
		searchParams,
		query,
		filter,
		sort,
		startIndex,
		limit,
		searchControl,
		flags,
	}: {
		searchParams: Record<string, any>;
		query: string;
		filter: SelectedUiFilters;
		sort: Sort;
		startIndex: number;
		limit: number;
		searchControl: Record<string, any>;
		flags: Array<string>;
	}): Promise<SearchResults> {
		return new Promise(async (resolve) => {
			// assign to local for brevity
			const hostContext: HostContext = this.searchHostContext;

			const searchables: Array<Searchable> = this._createSearchAssetsSearchables(
				filter,
				searchParams,
				searchControl,
			);

			// Create the SearchManager, giving it the Searchables
			const searchManager: SearchManager = new SearchManager(searchables, hostContext.imsInfo.accountType);
			// Kick off the initial search, and return a SearchResults promise
			const promises = [];
			promises.push(this.fileTypeService.fetchFileTypeData(this.searchHostContext.locale));
			promises.push(searchManager.search(query, filter, sort, startIndex, limit, flags));
			const allResults = await GlobalsUtils.checkAndExecutePromiseAllSettledPolyfill(promises);
			let results;

			if (allResults[1].status === 'fulfilled') {
				results = allResults[1].value;
			} else if (allResults[1].status === 'rejected') {
				results = allResults[1].reason;
			}
			const durations = results.getDurations();
			durations.forEach((durationObj) => {
				this.logger.log(
					'Search Service',
					`Search Assets ${durationObj.searchableName}: duration=${durationObj.duration}`,
				);
			});
			this.logger.log('Search Service', `Search Assets Service Total: duration=${results.getTotalDuration()}`);
			resolve(results);
		});
	}

	/**
	 * Retrieves the search json data from the Search Server.
	 * @param {object} searchRequestBody - the search request to perform ... FIXME: for now it is safe for this function to modify this body (caller has done an object copy)
	 * @param {string} endpointName - one of 'search' or 'search-assets'
	 * @returns {object|Error} - search datarequest response object, or an Error object if an invalid endpoint name was specified
	 */
	fetchSearchData(searchRequestBody: searchRequestBody, { authenticated }) {
		let result;

		if (!navigator.onLine) {
			result = {
				data: [],
			};
		} else {
			const endpointPath = authenticated ? '/v3/search' : '/v3/unauth/search';
			this.logger.log('Search Service', 'Making call to fetch Search Data Results');
			result = this._performSearchDataRequest(searchRequestBody, endpointPath, 'searchV3');
		}

		return result;
	}

	/**
	 * Retrieves the search history data for current user from the Search Server.
	 * @returns object - search datarequest response object
	 */
	fetchSearchHistory(searchHistoryCount: number) {
		return SearchHistory.fetchHistory({
			searchHistoryCount,
			searchHostContext: this.searchHostContext,
		});
	}

	/**
	 * Retrieves the App metaData from the Search Server.
	 * @returns object - search App Metadata response object
	 */
	fetchAppMetadata(locale: string) {
		return SearchApps.fetchAppMetadata({ locale, searchHostContext: this.searchHostContext });
	}

	/**
	 * Create/Update search history for current user from the Search Server.
	 * @returns object - search datarequest response object
	 */
	pushSearchHistory(searchQuery: string) {
		return SearchHistory.pushHistory({ searchHostContext: this.searchHostContext, searchQuery });
	}

	/**
	 * Delete search history for current user from the Search Server.
	 * @returns object - search datarequest response object
	 */
	deleteSearchHistory(searchQuery: string) {
		return SearchHistory.deleteHistory({ searchHostContext: this.searchHostContext, searchQuery });
	}

	/**
	 * Retrieves the search auto suggestion data for current user from the Search Server.
	 * @returns object - search datarequest response object
	 */
	async fetchSearchAutoComplete(searchQuery: string, searchConfig: any) {
		return await Autocomplete.performAutoCompletionsRequest({
			searchQuery,
			searchConfig,
		});
	}

	/**
	 * Retrieves the leo onboarding suggestion data for current user.
	 * @returns object - search datarequest response object
	 */
	async fetchLeoOnboarding(searchQuery: string, searchConfig: any) {
		return await Autocomplete.performLeoOnboardingRequest({
			searchQuery,
			searchConfig,
			searchHostContext: this.searchHostContext,
		});
	}

	/**
	 * Retrieves the leo onboarding suggestion data for current user.
	 * @returns object - search datarequest response object
	 */
	async fetchUserPreferences() {
		const userPreferences = await UserPreferences.performUserPreferencesRequest({
			searchHostContext: this.searchHostContext,
		});
		UserPreferencesModel.storeUserPreferences(userPreferences);
		return userPreferences;
	}

	/**
	 * Retrieves the leo onboarding suggestion data for current user.
	 * @returns object - search datarequest response object
	 */
	async storeUserPreferences(genAiPreference: boolean) {
		const storPreferenceResponse = await UserPreferences.performStoreUserPreferences({
			genAiPreference,
			searchHostContext: this.searchHostContext,
		});
		UserPreferencesModel.setGenAIPreference(genAiPreference);
		return storPreferenceResponse;
	}

	/**
	 * Retrieves the search auto suggestion data for current user from the Search Server.
	 * @returns object - search datarequest response object
	 */
	fetchSearchIntentsAutoComplete(searchQuery: string, searchConfig: any, dictionary: any): Promise<Array<Intent>> {
		return this._performIntentsForSearchQueryRequest(searchQuery, searchConfig, dictionary);
	}

	/**
	 * Retrieves the recents files data for current user from the browser storage or Search Server.
	 * @returns object -  recents files response object
	 */
	fetchRecentFiles(
		searchParams: Record<string, any>,
		fromCache: boolean,
		searchConfig?: any,
	): Promise<Array<Record<string, any>>> {
		return this._performRecentFilesRequest(searchParams, fromCache, searchConfig);
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: SearchService;
	searchHostContext: HostContext;
	logger: Logger;
	geoData: GeoData = {
		country: '',
		state: '',
		'Accept-Language': '',
	};
	fileTypeService: FileTypeService;

	constructor(searchHostContext: HostContext) {
		if (!SearchService.instance) {
			this.searchHostContext = searchHostContext;
			this.logger = getLogger();
			SearchService.instance = this;
			this.fileTypeService = new FileTypeService(searchHostContext.featureFlags, searchHostContext);
		}
		return SearchService.instance;
	}

	_createSearchAssetsSearchables(
		filter: SelectedUiFilters,
		searchParams: Record<string, any>,
		searchControl: Record<string, any>,
	): Array<Searchable> {
		return SearchFactory.createSearchableSet(this.searchHostContext, filter, searchParams, searchControl);
	}

	/**
	 * Retrieves the search json data from the Search Server.
	 * @param {bodyData} json body data
	 * @returns {Object} learn data request response object
	 */
	async _performSearchDataRequest(
		bodyData: Record<string, any>,
		path: string,
		endpointName: string,
	): Promise<SearchResult> {
		const env = this.searchHostContext.environment;
		const hostname = getConfig(env, 'SEARCH_SERVER');
		const curRequestId = uuid();
		SearchAnalytics.requestId = curRequestId;
		const postData = JSON.stringify(bodyData);
		const startTime = Date.now();
		const headers = {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			'x-api-key': this.searchHostContext.hostInfo.apiKey,
			authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
			'x-product': `${getConfig(env, 'APP_NAME')}/${getConfig(env, 'APP_VERSION')}`,
			'x-product-location': `${getConfig(env, 'APP_NAME')}.UniversalSearchService`,
			'x-request-id': curRequestId,
		};

		return new Promise((resolve) => {
			const request = https.request(
				{
					hostname,
					path,
					method: 'POST',
					headers,
				},
				(res: any) => {
					if (res.statusCode >= 300) {
						// On error, it should show the error status code
						const serverData = {
							serverResult: {
								url: `https://${hostname}${path}`,
								status: res.statusCode,
								message: res.statusMessage,
								requestId: curRequestId,
								requestBody: bodyData,
								duration: Date.now() - startTime,
							},
							hasServerError: true,
						};
						this.logger.error(
							'Search Service',
							'performSearchDataRequest Problem with Search Data Service response',
							{
								server: serverData,
								performanceTiming: PerformanceUtils.getPerformanceTimingByName(res.url),
							},
						);
						resolve(new SearchError(serverData.serverResult));
					} else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								const serverData = {
									serverResult: {
										url: `https://${hostname}${path}`,
										status: res.statusCode,
										message: res.statusMessage,
										requestId: curRequestId,
										requestBody: bodyData,
										duration: Date.now() - startTime,
									},
									hasServerError: false,
								};
								const serverEndpoint = {};
								serverEndpoint[endpointName] = serverData.serverResult.status;
								const serverStatusCodes = [serverEndpoint];
								const parsedResponseData = JSON.parse(data);

								if (parsedResponseData) {
									if (endpointName === 'searchV3') {
										if (
											Array.isArray(parsedResponseData.data) &&
											parsedResponseData.data.length > 0
										) {
											parsedResponseData.tracking &&
												(AnalyticsUtil.NERTrackingData = parsedResponseData.tracking);
											const upstreamResults = [];
											parsedResponseData.data.forEach((data) => {
												delete data.attributes;

												if (data && data.searchResults && data.searchResults.serverResult) {
													const upstreamResult = {
														id: data.id,
													};
													const upstreamEndpoint = {};
													Object.assign(upstreamResult, data.searchResults.serverResult);
													upstreamResults.push(upstreamResult);
													upstreamEndpoint[data.id] = upstreamResult.status;
													serverStatusCodes.push(upstreamEndpoint);
												}
											});

											if (Array.isArray(upstreamResults) && upstreamResults.length > 0) {
												serverData.upstreamResults = upstreamResults;
											}
										}
									} else if (endpointName === 'search-assets') {
										if (parsedResponseData.serverResult) {
											const upstreamResults = [];
											const upstreamEndpoint = {};
											const upstreamResult = {
												id: 'uss',
											};
											Object.assign(upstreamResult, parsedResponseData.serverResult);
											upstreamResults.push(upstreamResult);
											serverData.upstreamResults = upstreamResults;
											upstreamEndpoint.uss = upstreamResult.status;
											serverStatusCodes.push(upstreamEndpoint);
										}
									}
								}

								if (serverStatusCodes.length > 0) {
									const statusCodes = serverStatusCodes.map((data) => Object.values(data)[0]);
									serverData.hasServerError = statusCodes.some(
										(statusCode) => typeof statusCode !== 'number' || statusCode >= 300,
									);
								}

								this.logger.info(
									'Search Service',
									`performSearchDataRequest ${endpointName} endpoint response`,
									{
										server: serverData,
										performanceTiming: PerformanceUtils.getPerformanceTimingByName(res.url),
									},
								);
								resolve(parsedResponseData);
							} catch (e) {
								// FIXME: why are we resolving with malformed JSON data?
								resolve(data);
							}
						});
					}
				},
			);
			request.on('error', (err) => {
				// on error, display the search error message
				const serverData = {
					serverResult: {
						url: `https://${hostname}${path}`,
						status: 408,
						message: err.message,
						requestId: curRequestId,
						requestBody: bodyData,
						duration: Date.now() - startTime,
					},
					hasServerError: true,
				};
				this.logger.error(
					'Search Service',
					'performSearchDataRequest Problem with Search Data Service request',
					{
						server: serverData,
					},
				);
				resolve(new SearchError(serverData.serverResult));
			});
			request.write(postData);
			request.end();
		});
	}

	/** TODO : implement the original request when QIS implements intents shape
	 * [fetches intents matching the query from searchbar]
	 * @param  {HostContext} searchHostContext
	 * @param  {[Intent]} intents
	 * @param  {string} query
	 * @returns {[Intent]} filteredIntents
	 */
	async _performIntentsForSearchQueryRequest(query: string, searchConfig: any, dictionary: any): Promise<any> {
		const startTime = Date.now();
		try {
			const env = this.searchHostContext.environment;
			const locale = this.searchHostContext.locale.toLowerCase();
			const autoCompleteConfig: any = searchConfig?.ui?.autoComplete?.[env] ?? {};
			const limit: number = autoCompleteConfig?.intent?.limit ?? 3;
			const minimumMatchLength: number = autoCompleteConfig?.intent?.minimumMatchLength ?? 3;
			const localeMinMatchLength: number = autoCompleteConfig?.intent?.localeMinMatchLength?.[locale] ?? 0;
			const searchQuery = query.toLowerCase();
			const searchQueryArray = searchQuery.split(' ');
			let filteredIntents: Intent[] = [];

			if (!query) {
				filteredIntents = [];
			} else {
				let I2AAutocompleteCache = {};

				const I2ASessionData = WebStorage.sessionStorage.getItem('asde.autocomplete.I2A');
				I2AAutocompleteCache = I2ASessionData.queries ? I2ASessionData : { queries: {} };
				searchQueryArray.forEach((q, index) => {
					//	const queryTerm = FileTypeService.fileTypeDataObject.translations.intents[q];
					const filteredIntentQueries: string[] =
						I2AAutocompleteCache.queries?.[q] ||
						GlobalsUtils.autoComplete(dictionary, q, {
							minmatchlength: localeMinMatchLength || minimumMatchLength,
						});
					const queryBasedIntents: string[] = [];
					for (const filteredQuery of filteredIntentQueries) {
						const intents = SearchUtils.searchQueryIntentsMap[filteredQuery];
						queryBasedIntents.push(...intents);
					}

					I2AAutocompleteCache.queries[q] = filteredIntentQueries;

					if (!filteredIntents.length) {
						filteredIntents.push(...queryBasedIntents);
					} else {
						const commonIntents = queryBasedIntents.filter((intent) =>
							filteredIntents.some((filteredIntent) => filteredIntent.id === intent.id),
						);
						filteredIntents.unshift(...[...commonIntents, ...queryBasedIntents]);
					}
				});
				WebStorage.sessionStorage.setItem('asde.autocomplete.I2A', I2AAutocompleteCache);
			}

			// const distinctFilteredIntentMap: Record<string, Intent> = filteredIntents.reduce((acc, intent) => {
			// 	if (acc[intent.id]) {
			// 		return acc;
			// 	}
			// 	intent.matchScore = 0;
			// 	const enIntentQueries = intent.query.toLowerCase();
			// 	const translatedIntentQueries = intent.query
			// 		.toLocaleLowerCase(locale)
			// 		.split(' ')
			// 		.map((q) => FileTypeService.fileTypeDataObject.translations.intents[q])
			// 		.join(' ');
			// 	searchQueryArray
			// 		.map((q) => q.toLowerCase())
			// 		.forEach((searchQuery) => {
			// 			if (enIntentQueries.includes(searchQuery) || translatedIntentQueries.includes(searchQuery)) {
			// 				intent.matchScore += 1;
			// 			}
			// 		});
			// 	acc[intent.id] = intent;
			// 	return acc;
			// }, {});

			const distinctFilteredIntents: Intent[] = filteredIntents || Object.values(distinctFilteredIntentMap);
			this.logger.log('Search Service', ':_performIntentsForSearchQueryRequest', distinctFilteredIntents);
			distinctFilteredIntents.sort(
				(a: Intent, b: Intent) => b?.matchScore - a?.matchScore || a?.score - b?.score,
			);
			return distinctFilteredIntents.slice(0, limit);
		} catch (err) {
			const loggerData = {
				message: err.message,
				duration: Date.now() - startTime,
			};
			this.logger.error('Search Service', 'Problem with Search Intents fetch request', loggerData);
			const searchServiceError = new Error('_performIntentsForSearchQueryRequest error');
			return Promise.reject(searchServiceError);
		}
	}

	/**
	 * Fetches recent files for the user
	 * @param  {searchParams} Record
	 * @param  {fromCache} boolean
	 * @param  {searchConfig?} any
	 * @returns {any} Recent User files
	 */
	async _performRecentFilesRequest(searchParams: Record<string, any>, fromCache: boolean, searchConfig?: any): any {
		const startTime = Date.now();
		try {
			const env = this.searchHostContext.environment;
			const recentFilesConfig: any = searchConfig?.ui?.recentFiles?.[env] ?? {};
			const limit: number = recentFilesConfig?.limit ?? 6;
			const cachedRecentFiles = SearchUtils.getRecentFilesFromWebStorage();
			let indexedRecentFiles = [];
			if (!fromCache || !cachedRecentFiles?.length) {
				const hostContext: HostContext = this.searchHostContext;
				const searchables: Array<Searchable> = this._createSearchAssetsSearchables(
					{},
					searchParams,
					searchConfig?.searchControl ?? {},
				);

				// Create the SearchManager, giving it the Searchables
				const searchManager: SearchManager = new SearchManager(searchables, hostContext.imsInfo.accountType);

				const searchAssetsResult = await searchManager.search(
					'',
					{},
					{
						sortBy: 'PupaAccessDate',
						sortOrder: 'Descending',
					},
					0,
					limit,
				);
				const resultSources: ResultSource[] = searchAssetsResult.getResultSources();
				indexedRecentFiles = resultSources[0].resultItems;
				SearchUtils.setRecentFilesToWebStorage(indexedRecentFiles);

				return indexedRecentFiles;
			}

			return cachedRecentFiles;
		} catch (err) {
			const loggerData = {
				message: err.message,
				duration: Date.now() - startTime,
			};
			this.logger.error('Search Service', 'Problem with Search Recent Files fetch', loggerData);
			const searchServiceError = new Error('_performRecentFilesRequest error');
			return Promise.reject(searchServiceError);
		}
	}
}

export default SearchService;
